import React, { memo, useCallback, useMemo } from "react";
import useMessageThreadDraft from "modules/hooks/use-message-thread-draft";
import AddItemButton from "../components/add-item-button";
import CopyListPopover from "./copy-list-popover";
import FileSelectionRow from "../components/file-selection-row";
import {
  MessageThreadFilesDrawerQuery,
  MessageThreadFilesDrawerQueryVariables,
} from "gql/graphql";
import { useQuery } from "@apollo/client";
import MESSAGE_THREAD_FILES_DRAWER_QUERY from "../../messaging/drawers/files-drawer/_networking/message-thread-files-drawer";
import { IRelatedMessageThread } from "modules/providers/index-db-provider/index-db-models";

interface IProps {
  draftId: string;
  relatedMessageThread: IRelatedMessageThread;
}

const CopyFilesPopover: React.FC<IProps> = ({
  draftId,
  relatedMessageThread,
}) => {
  const { files: draftFiles, copyFiles } = useMessageThreadDraft({ draftId });

  const { data } = useQuery<
    MessageThreadFilesDrawerQuery,
    MessageThreadFilesDrawerQueryVariables
  >(MESSAGE_THREAD_FILES_DRAWER_QUERY, {
    variables: {
      id: relatedMessageThread.id,
    },
    skip: !relatedMessageThread,
  });

  const files = useMemo(
    () =>
      (data?.messageThread?.fileList ?? []).filter(
        (file) => !draftFiles?.some((draftFile) => draftFile.key === file.key),
      ) ?? [],
    [data?.messageThread?.fileList, draftFiles],
  );

  const onSaveClicked = useCallback(
    (selectedIds: string[]) => {
      copyFiles(...files.filter((file) => selectedIds.includes(file.id)));
    },
    [copyFiles, files],
  );

  const renderRow = useCallback(
    (_: number, rowIndex: number) => {
      if (files.length === 0) {
        return null;
      }
      return (
        <FileSelectionRow
          file={files[rowIndex]}
          isLast={rowIndex === files.length - 1}
        />
      );
    },
    [files],
  );

  const isDisabled = useMemo(() => files.length === 0, [files.length]);

  const title = useMemo(() => {
    if (files.length > 0) {
      return `Attachments (${files.length})`;
    }
    return "Attachments";
  }, [files.length]);

  return (
    <CopyListPopover
      sections={[
        {
          ids: files.map((file) => file.id),
          title: "Attachments",
        },
      ]}
      title="Add Attachments"
      saveTitle="Add Attachments"
      renderRow={renderRow}
      onSaveClicked={onSaveClicked}
    >
      <AddItemButton isDisabled={isDisabled}>{title}</AddItemButton>
    </CopyListPopover>
  );
};

export default memo(CopyFilesPopover);
